import { useState, useRef } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, XCircle, Loader2, Play, RotateCcw,
  ChevronDown, ChevronUp, FlaskConical, StopCircle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import CCBSkillHint from "@/components/CCBSkillHint";

const BASE = process.env.REACT_APP_BACKEND_URL;

// ── Types ──────────────────────────────────────────────────────────────────

type TestStatus = "idle" | "running" | "passed" | "failed" | "skipped";

interface TestCase {
  id: number;
  name: string;
  group: string;
  run: (ctx: RunContext) => Promise<void>;
}

interface TestResult {
  id: number;
  status: TestStatus;
  error?: string;
  duration?: number;
}

interface RunContext {
  authHeader: Record<string, string>;
  projectId: string | null;
  log: (msg: string) => void;
}

// ── Test definitions ────────────────────────────────────────────────────────

function buildTests(mongoProjectId: string): TestCase[] {
  return [
    // ── Authentication ──────────────────────────────────────────────────
    {
      id: 1, group: "Authentication", name: "Unauthenticated request returns 401",
      run: async ({ authHeader }) => {
        const r = await axios.get(`${BASE}/api/tools/projects/00000000-0000-0000-0000-000000000000`, {
          validateStatus: () => true,
        });
        if (r.status !== 401 && r.status !== 403 && r.status !== 422)
          throw new Error(`Expected 401/403, got ${r.status}`);
      }
    },
    {
      id: 2, group: "Authentication", name: "Expired/invalid token returns 401",
      run: async () => {
        const r = await axios.get(`${BASE}/api/tools/projects/00000000-0000-0000-0000-000000000000`, {
          headers: { Authorization: "Bearer invalid.token.here" },
          validateStatus: () => true,
        });
        if (r.status !== 401 && r.status !== 403 && r.status !== 422)
          throw new Error(`Expected 401, got ${r.status}`);
      }
    },
    {
      id: 3, group: "Authentication", name: "Valid token returns project data",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project found — visit Requirements tab first");
        const r = await axios.get(`${BASE}/api/tools/projects/${projectId}`, {
          headers: authHeader, validateStatus: () => true
        });
        if (r.status !== 200) throw new Error(`Expected 200, got ${r.status}`);
        if (!r.data.requirements) throw new Error("Response missing requirements field");
      }
    },

    // ── Requirements ────────────────────────────────────────────────────
    {
      id: 4, group: "Requirements", name: "Create requirement with all fields",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.post(`${BASE}/api/tools/requirements`, {
          project_id: projectId, title: "E2E Test Requirement",
          raw_text: "Test notes", feature: "testing", channel: "portal",
          contains_phi: false, market: null, sensitivity_flags: [],
          req_type: "functional", assumptions: [], constraints_list: [],
          domain: "e2e", priority: "should"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Create failed: ${r.status} — ${JSON.stringify(r.data)}`);
        if (!r.data.id) throw new Error("Response missing id");
      }
    },
    {
      id: 5, group: "Requirements", name: "Reject empty title",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.post(`${BASE}/api/tools/requirements`, {
          project_id: projectId, title: "", req_type: "functional"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status === 200) throw new Error("Should have rejected empty title but returned 200");
      }
    },
    {
      id: 6, group: "Requirements", name: "List requirements for project",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.get(`${BASE}/api/tools/projects/${projectId}`, {
          headers: authHeader, validateStatus: () => true
        });
        if (r.status !== 200) throw new Error(`Expected 200, got ${r.status}`);
        if (!Array.isArray(r.data.requirements)) throw new Error("requirements is not an array");
      }
    },
    {
      id: 7, group: "Requirements", name: "Requirement has domain and priority fields",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.get(`${BASE}/api/tools/projects/${projectId}`, { headers: authHeader });
        const reqs = r.data.requirements || [];
        if (reqs.length === 0) throw new Error("No requirements to inspect — create one first");
        const req = reqs[0];
        if (!("domain" in req)) throw new Error("Missing domain field");
        if (!("priority" in req)) throw new Error("Missing priority field");
      }
    },
    {
      id: 8, group: "Requirements", name: "Update requirement (PATCH) snapshots version",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        // Create a fresh req
        const create = await axios.post(`${BASE}/api/tools/requirements`, {
          project_id: projectId, title: "Version Test Req",
          req_type: "functional"
        }, { headers: authHeader, validateStatus: () => true });
        if (create.status !== 200) throw new Error(`Create failed: ${create.status}`);
        const reqId = create.data.id;
        // Patch it
        const patch = await axios.patch(`${BASE}/api/tools/requirements/${reqId}`,
          { title: "Version Test Req — Updated" },
          { headers: authHeader, validateStatus: () => true }
        );
        if (patch.status !== 200) throw new Error(`Patch failed: ${patch.status}`);
        if ((patch.data.version ?? 1) < 2) throw new Error("Version not incremented");
        // Check versions endpoint
        const versions = await axios.get(`${BASE}/api/tools/requirements/${reqId}/versions`,
          { headers: authHeader, validateStatus: () => true }
        );
        if (versions.status !== 200) throw new Error(`Versions endpoint: ${versions.status}`);
        if (!Array.isArray(versions.data) || versions.data.length === 0)
          throw new Error("No version snapshots stored");
      }
    },
    {
      id: 9, group: "Requirements", name: "Assign requirement to team member",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const proj = await axios.get(`${BASE}/api/tools/projects/${projectId}`, { headers: authHeader });
        const reqs = proj.data.requirements || [];
        if (reqs.length === 0) throw new Error("No requirements to assign");
        const reqId = reqs[0].id;
        const r = await axios.patch(`${BASE}/api/tools/requirements/${reqId}/assign`,
          { assigned_to: "Test BA" },
          { headers: authHeader, validateStatus: () => true }
        );
        if (r.status !== 200) throw new Error(`Assign failed: ${r.status}`);
        if (r.data.assigned_to !== "Test BA") throw new Error("assigned_to not updated");
      }
    },

    // ── Compliance ──────────────────────────────────────────────────────
    {
      id: 10, group: "Compliance", name: "PHI + email channel triggers violation",
      run: async ({ authHeader }) => {
        const r = await axios.post(`${BASE}/api/tools/evaluate-rules`, {
          contains_phi: true, channel: "email",
          sensitivity_flags: [], market: null, feature: "notification"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Rule engine: ${r.status}`);
        if (!r.data.violations?.length) throw new Error("Expected at least 1 violation for PHI+email");
      }
    },
    {
      id: 11, group: "Compliance", name: "Florida + breach flag triggers violation",
      run: async ({ authHeader }) => {
        const r = await axios.post(`${BASE}/api/tools/evaluate-rules`, {
          contains_phi: false, channel: null,
          sensitivity_flags: ["breach"], market: "Florida", feature: "notification"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Rule engine: ${r.status}`);
        if (!r.data.violations?.length && !r.data.warnings?.length)
          throw new Error("Expected violations/warnings for Florida+breach");
      }
    },
    {
      id: 12, group: "Compliance", name: "payment_amount flag triggers internal policy block",
      run: async ({ authHeader }) => {
        const r = await axios.post(`${BASE}/api/tools/evaluate-rules`, {
          contains_phi: false, channel: "email",
          sensitivity_flags: ["payment_amount"], market: null, feature: "payment"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Rule engine: ${r.status}`);
        const allIssues = [...(r.data.violations || []), ...(r.data.warnings || [])];
        if (!allIssues.length) throw new Error("Expected violation for payment_amount in email");
      }
    },
    {
      id: 13, group: "Compliance", name: "Clean requirement passes rule engine",
      run: async ({ authHeader }) => {
        const r = await axios.post(`${BASE}/api/tools/evaluate-rules`, {
          contains_phi: false, channel: "portal",
          sensitivity_flags: [], market: null, feature: "dashboard"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Rule engine: ${r.status}`);
        if (!r.data.passed) throw new Error("Clean requirement should pass all rules");
      }
    },
    {
      id: 14, group: "Compliance", name: "Compliance results endpoint returns data",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const proj = await axios.get(`${BASE}/api/tools/projects/${projectId}`, { headers: authHeader });
        const reqs = proj.data.requirements || [];
        if (reqs.length === 0) throw new Error("No requirements to analyse");
        // Just check legal-analysis endpoint is reachable
        const r = await axios.get(`${BASE}/api/tools/legal-analysis/${reqs[0].id}`,
          { headers: authHeader, validateStatus: () => true }
        );
        if (r.status !== 200) throw new Error(`Legal analysis GET: ${r.status}`);
      }
    },

    // ── Test Cases ──────────────────────────────────────────────────────
    {
      id: 15, group: "Test Cases", name: "Create and retrieve a test case",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const create = await axios.post(`${BASE}/api/tools/test-cases`, {
          project_id: projectId, title: "E2E Test Case",
          feature_tag: "@e2e",
          scenarios: [{ name: "Happy path", tags: ["@smoke"], given: ["setup"], when: ["action"], then: ["result"] }],
          status: "draft", framework: "gherkin"
        }, { headers: authHeader, validateStatus: () => true });
        if (create.status !== 200) throw new Error(`Create test case: ${create.status}`);
        const tcId = create.data.id;
        // List
        const list = await axios.get(`${BASE}/api/tools/test-cases?project_id=${projectId}`,
          { headers: authHeader, validateStatus: () => true }
        );
        if (list.status !== 200) throw new Error(`List test cases: ${list.status}`);
        const found = list.data.find((tc: any) => tc.id === tcId);
        if (!found) throw new Error("Created test case not found in list");
      }
    },
    {
      id: 16, group: "Test Cases", name: "Update test case status",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const list = await axios.get(`${BASE}/api/tools/test-cases?project_id=${projectId}`,
          { headers: authHeader });
        if (!list.data.length) throw new Error("No test cases to update");
        const tcId = list.data[0].id;
        const patch = await axios.patch(`${BASE}/api/tools/test-cases/${tcId}`,
          { status: "review" },
          { headers: authHeader, validateStatus: () => true }
        );
        if (patch.status !== 200) throw new Error(`Patch: ${patch.status}`);
        if (patch.data.status !== "review") throw new Error("Status not updated");
      }
    },
    {
      id: 17, group: "Test Cases", name: "Delete test case",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        // Create one to delete
        const create = await axios.post(`${BASE}/api/tools/test-cases`, {
          project_id: projectId, title: "To Delete",
          feature_tag: "@delete", scenarios: [], status: "draft", framework: "gherkin"
        }, { headers: authHeader });
        const del = await axios.delete(`${BASE}/api/tools/test-cases/${create.data.id}`,
          { headers: authHeader, validateStatus: () => true });
        if (del.status !== 200) throw new Error(`Delete: ${del.status}`);
      }
    },

    // ── Team & Schedule ─────────────────────────────────────────────────
    {
      id: 18, group: "Team & Schedule", name: "Fetch team from project",
      run: async ({ authHeader }) => {
        const r = await axios.get(`${BASE}/api/projects`, {
          headers: authHeader, validateStatus: () => true
        });
        if (r.status !== 200) throw new Error(`Projects list: ${r.status}`);
      }
    },
    {
      id: 19, group: "Team & Schedule", name: "Log hours endpoint accepts valid data",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.post(`${BASE}/api/tools/hour-logs`, {
          project_id: projectId, ba_name: "Test BA",
          hours: 4, logged_date: new Date().toISOString().slice(0, 10), note: "e2e test"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Log hours: ${r.status} — ${JSON.stringify(r.data)}`);
        if (!r.data.id) throw new Error("Missing id in response");
      }
    },
    {
      id: 20, group: "Team & Schedule", name: "Retrieve hour logs for project",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.get(`${BASE}/api/tools/hour-logs?project_id=${projectId}`,
          { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Hour logs: ${r.status}`);
        if (!Array.isArray(r.data)) throw new Error("Expected array");
      }
    },
    {
      id: 21, group: "Team & Schedule", name: "Delete hour log",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        // Create one first
        const create = await axios.post(`${BASE}/api/tools/hour-logs`, {
          project_id: projectId, ba_name: "Test BA",
          hours: 1, logged_date: new Date().toISOString().slice(0, 10)
        }, { headers: authHeader });
        const del = await axios.delete(`${BASE}/api/tools/hour-logs/${create.data.id}`,
          { headers: authHeader, validateStatus: () => true });
        if (del.status !== 200) throw new Error(`Delete: ${del.status}`);
      }
    },

    // ── Knowledge Base ──────────────────────────────────────────────────
    {
      id: 22, group: "Knowledge Base", name: "Document search endpoint reachable",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.get(
          `${BASE}/api/tools/search?query=test&project_id=${projectId}`,
          { headers: authHeader, validateStatus: () => true }
        );
        // 200 or 404 (no docs) are both acceptable — just not 500
        if (r.status === 500) throw new Error("Search returned 500");
      }
    },
    {
      id: 23, group: "Knowledge Base", name: "Document list endpoint reachable",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.get(
          `${BASE}/api/tools/documents?project_id=${projectId}`,
          { headers: authHeader, validateStatus: () => true }
        );
        if (r.status === 500) throw new Error("Documents list returned 500");
      }
    },

    // ── AI Endpoints ────────────────────────────────────────────────────
    {
      id: 24, group: "AI Endpoints", name: "Structure-requirement endpoint reachable",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.post(`${BASE}/api/tools/structure-requirement`, {
          raw_text: "We need a login page", project_id: projectId
        }, { headers: authHeader, validateStatus: () => true });
        // 200 (Ollama online) or 503/500 (Ollama offline) — both reachable
        if (r.status === 404) throw new Error("Endpoint not found — route missing");
      }
    },
    {
      id: 25, group: "AI Endpoints", name: "Meeting-intelligence endpoint reachable",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.post(`${BASE}/api/tools/meeting-intelligence`, {
          transcript: "We discussed login", project_id: projectId
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status === 404) throw new Error("Endpoint not found");
      }
    },
    {
      id: 26, group: "AI Endpoints", name: "Generate-test-cases endpoint reachable",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const proj = await axios.get(`${BASE}/api/tools/projects/${projectId}`, { headers: authHeader });
        const reqs = proj.data.requirements || [];
        if (reqs.length === 0) throw new Error("No requirements — create one first");
        const r = await axios.post(`${BASE}/api/tools/generate-test-cases`, {
          requirement_id: reqs[0].id, project_id: projectId
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status === 404) throw new Error("Endpoint not found");
      }
    },
    {
      id: 27, group: "AI Endpoints", name: "Legal-analysis endpoint reachable",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const proj = await axios.get(`${BASE}/api/tools/projects/${projectId}`, { headers: authHeader });
        const reqs = proj.data.requirements || [];
        if (reqs.length === 0) throw new Error("No requirements — create one first");
        const r = await axios.post(`${BASE}/api/tools/legal-analysis`, {
          requirement_id: reqs[0].id, project_id: projectId
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status === 404) throw new Error("Endpoint not found");
      }
    },

    // ── Agent Log ───────────────────────────────────────────────────────
    {
      id: 28, group: "Agent Log", name: "Log agent run and retrieve",
      run: async ({ authHeader, projectId }) => {
        if (!projectId) throw new Error("No BA project");
        const r = await axios.post(`${BASE}/api/tools/log-run`, {
          project_id: projectId, agent_name: "e2e_test_agent",
          input: { test: true }, output: { result: "ok" },
          model: "test", tools_called: ["test"], duration_ms: 1, status: "success"
        }, { headers: authHeader, validateStatus: () => true });
        if (r.status !== 200) throw new Error(`Log run: ${r.status}`);
        // Retrieve
        const get = await axios.get(
          `${BASE}/api/tools/agent-runs?project_id=${projectId}`,
          { headers: authHeader, validateStatus: () => true }
        );
        if (get.status !== 200) throw new Error(`Agent runs GET: ${get.status}`);
      }
    },

    // ── Edge Cases ──────────────────────────────────────────────────────
    {
      id: 29, group: "Edge Cases", name: "Non-existent requirement returns 404",
      run: async ({ authHeader }) => {
        const r = await axios.get(
          `${BASE}/api/tools/requirements/00000000-0000-0000-0000-000000000000/versions`,
          { headers: authHeader, validateStatus: () => true }
        );
        if (r.status === 200 && Array.isArray(r.data) && r.data.length === 0) return; // empty is ok
        if (r.status !== 404 && r.status !== 200)
          throw new Error(`Expected 404 or empty, got ${r.status}`);
      }
    },
    {
      id: 30, group: "Edge Cases", name: "Backend health check responds",
      run: async () => {
        const r = await axios.get(`${BASE}/health`,
          { validateStatus: () => true }
        );
        if (r.status !== 200) throw new Error(`Health check: ${r.status}`);
      }
    },
  ];
}

// ── Status icon ─────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: TestStatus }) {
  if (status === "passed")  return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
  if (status === "failed")  return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-accent shrink-0" />;
  if (status === "skipped") return <div className="h-4 w-4 rounded-full bg-muted shrink-0" />;
  return <div className="h-4 w-4 rounded-full border-2 border-border shrink-0" />;
}

// ── Main panel ───────────────────────────────────────────────────────────────

interface Props {
  mongoProjectId: string;
  baProjectId: string | null;
}

export default function TestRunnerPanel({ mongoProjectId, baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const tests = buildTests(mongoProjectId);
  const [results, setResults] = useState<Record<number, TestResult>>({});
  const [running, setRunning] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const abortRef = useRef(false);

  const groups = Array.from(new Set(tests.map(t => t.group)));

  const setResult = (id: number, update: Partial<TestResult>) =>
    setResults(prev => ({ ...prev, [id]: { id, status: "idle", ...prev[id], ...update } }));

  const runTest = async (test: TestCase) => {
    setResult(test.id, { status: "running", error: undefined });
    const start = Date.now();
    const logs: string[] = [];
    try {
      await test.run({
        authHeader,
        projectId: baProjectId,
        log: (msg) => logs.push(msg),
      });
      setResult(test.id, { status: "passed", duration: Date.now() - start });
    } catch (e: any) {
      setResult(test.id, {
        status: "failed",
        error: e?.response?.data?.detail || e?.message || String(e),
        duration: Date.now() - start,
      });
    }
  };

  const runAll = async () => {
    abortRef.current = false;
    setRunning(true);
    // Reset
    const fresh: Record<number, TestResult> = {};
    tests.forEach(t => { fresh[t.id] = { id: t.id, status: "idle" }; });
    setResults(fresh);

    for (const test of tests) {
      if (abortRef.current) break;
      await runTest(test);
    }
    setRunning(false);
  };

  const runFailed = async () => {
    abortRef.current = false;
    setRunning(true);
    const failed = tests.filter(t => results[t.id]?.status === "failed");
    for (const test of failed) {
      if (abortRef.current) break;
      await runTest(test);
    }
    setRunning(false);
  };

  const stopRun = () => { abortRef.current = true; };

  // Summary counts
  const total   = tests.length;
  const passed  = Object.values(results).filter(r => r.status === "passed").length;
  const failed  = Object.values(results).filter(r => r.status === "failed").length;
  const ran     = Object.values(results).filter(r => r.status !== "idle").length;
  const pct     = ran ? Math.round((passed / ran) * 100) : 0;

  return (
    <div className="p-4 space-y-4 max-w-2xl">
    <CCBSkillHint tab="test-runner" className="mb-4" />
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <FlaskConical className="h-4 w-4" /> Test Runner
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} integration tests · runs against live backend · no terminal needed
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {failed > 0 && !running && (
            <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={runFailed}>
              <RotateCcw className="h-3.5 w-3.5" /> Re-run Failed ({failed})
            </Button>
          )}
          {running ? (
            <Button size="sm" variant="destructive" className="gap-1.5" onClick={stopRun}>
              <StopCircle className="h-3.5 w-3.5" /> Stop
            </Button>
          ) : (
            <Button size="sm" className="gap-1.5" onClick={runAll}>
              <Play className="h-3.5 w-3.5" /> Run All {total} Tests
            </Button>
          )}
        </div>
      </div>

      {/* Summary bar */}
      {ran > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs font-mono">
            <div className="flex gap-3">
              <span className="text-green-600">{passed} passed</span>
              {failed > 0 && <span className="text-destructive">{failed} failed</span>}
              <span className="text-muted-foreground">{ran}/{total} run</span>
            </div>
            <span className={pct === 100 ? "text-green-600 font-semibold" : failed > 0 ? "text-destructive" : "text-muted-foreground"}>
              {pct}%
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-border overflow-hidden flex">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${(passed / total) * 100}%` }} />
            <div className="h-full bg-destructive transition-all" style={{ width: `${(failed / total) * 100}%` }} />
          </div>
        </div>
      )}

      {!baProjectId && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-700">
          Visit the Requirements tab first to initialise the backend project — some tests require a project ID.
        </div>
      )}

      {/* Test groups */}
      <div className="space-y-4">
        {groups.map(group => {
          const groupTests = tests.filter(t => t.group === group);
          const gPassed = groupTests.filter(t => results[t.id]?.status === "passed").length;
          const gFailed = groupTests.filter(t => results[t.id]?.status === "failed").length;

          return (
            <div key={group}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-mono font-semibold text-muted-foreground uppercase tracking-wider">{group}</span>
                <span className="text-[10px] font-mono text-muted-foreground/60">{groupTests.length}</span>
                {gPassed > 0 && <span className="text-[10px] font-mono text-green-600">{gPassed} ✓</span>}
                {gFailed > 0 && <span className="text-[10px] font-mono text-destructive">{gFailed} ✗</span>}
                <div className="flex-1 h-px bg-border" />
                <Button
                  size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-muted-foreground"
                  disabled={running}
                  onClick={async () => {
                    setRunning(true);
                    for (const t of groupTests) await runTest(t);
                    setRunning(false);
                  }}
                >
                  <Play className="h-3 w-3 mr-1" /> Run group
                </Button>
              </div>

              <div className="space-y-1">
                {groupTests.map(test => {
                  const res = results[test.id];
                  const status: TestStatus = res?.status || "idle";
                  const isExpanded = expandedId === test.id;

                  return (
                    <div key={test.id} className={`rounded-md border bg-card transition-colors ${status === "failed" ? "border-destructive/40" : status === "passed" ? "border-green-500/20" : "border-border"}`}>
                      <div
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : test.id)}
                      >
                        <StatusIcon status={status} />
                        <span className="text-xs text-foreground flex-1">{test.name}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          {res?.duration !== undefined && (
                            <span className="text-[10px] font-mono text-muted-foreground">{res.duration}ms</span>
                          )}
                          {status === "idle" && !running && (
                            <Button
                              size="sm" variant="ghost"
                              className="h-6 w-6 p-0 text-muted-foreground"
                              onClick={(e) => { e.stopPropagation(); runTest(test); }}
                            >
                              <Play className="h-3 w-3" />
                            </Button>
                          )}
                          {res?.error && (
                            isExpanded
                              ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                              : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      {isExpanded && res?.error && (
                        <div className="px-3 pb-2.5 pt-0">
                          <pre className="text-[11px] text-destructive font-mono bg-destructive/5 rounded p-2 whitespace-pre-wrap border border-destructive/20">
                            {res.error}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
